<template>
  <div class="h-full w-full flex flex-col bg-white">
    <div class="flex-1 relative">
      <div id="cottage-list-map" ref="leafletMap" class="h-full w-full" />

      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-200 ease-in"
        enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-full opacity-0"
      >
        <div
          v-if="selectedCottage && !isDesktop"
          class="absolute bottom-0 left-0 right-0 z-1000 p-4 pointer-events-none md:hidden"
        >
          <div class="max-w-sm mx-auto pointer-events-auto flex justify-center">
            <CottageListMapPopup
              :cottage="selectedCottage"
              show-close
              @close="closePanel"
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createApp, h } from 'vue'
import type {
  MapCottageType,
  schemaType as mapSchemaType,
} from '~~/shared/api/rurality/v3/search/cottages/map/schema'
import { browserFetch as fetchMapCottagesEndpoint } from '~~/modules/api/rurality/v3/search/cottages/map'
import type { paramsType } from '~~/shared/api/rurality/v3/search/cottages/map/paramsSchema'
import { useSendCottageStats } from '~~/modules/views/cottage-list/composables/useSendCottageStats'
import type { schemaType as DestinationSchemaType } from '~~/shared/api/rurality/v3/destinations/schema'
import { toCoords } from '~~/modules/views/cottage-list/utils/toCoords'
import CottageListMapPopup from './CottageListMapPopup.vue'
import { UBadge } from '#components'

const props = defineProps<{
  targetCottage?: MapCottageType | null
  destination?: DestinationSchemaType['destination'] | null
}>()

interface CustomMapOptions {
  center?: [number, number]
  zoom?: number
  minZoom?: number
  maxZoom?: number
  fullscreenControl?: boolean
  zoomControl?: boolean
}

const route = useRoute()
const router = useRouter()
const { isDesktop } = useDevice()
const { sendMapCottageStatsCloudwatch, sendMapCottageCardViz } =
  useSendCottageStats()

const map = ref<any>(null)
const leafletMap = ref<HTMLElement | null>(null)
const markersMap = ref<Map<string, any>>(new Map())
const popupApps = ref<Map<string, any>>(new Map())
const cottages = ref<MapCottageType[]>([])
const initialCottages = ref<MapCottageType[]>([])
const isLoading = ref(true)
const cottagesData = ref<mapSchemaType | undefined>(undefined)
const selectedCottage = ref<MapCottageType | null>(null)
const isInitialLoad = ref(true)
const isUpdatingBounds = ref(false)
const hasLocationParameter = ref(false)
const visitedCottages = ref<Set<string>>(new Set())
const apiBounds = ref<MetaSuccessRuralitySchemaType['bounds'] | null>(null)
const destinationMarker = ref<any>(null)
let L: any = null
let updateBoundsTimeout: NodeJS.Timeout | null = null
let abortController: AbortController | null = null

const defaultCenter: [number, number] = [40.4168, -3.7038]
const zoom = 6
const minZoom = 5
const maxZoom = 19

function parseUrlParams(): Omit<paramsType, 'ne' | 'sw'> {
  const query = route.query
  return {
    page: query.page ? Number(query.page) : 1,
    l: (query.l as string) || undefined,
    f: (query.f as string) || undefined,
    min_price: query.min_price ? Number(query.min_price) * 100 : undefined,
    max_price: query.max_price ? Number(query.max_price) * 100 : undefined,
    d: (query.d as string) || undefined,
    c: query.c ? Number(query.c) : undefined,
    a: (query.a as string) || undefined,
    cor: (query.cor as string) || undefined,
    ec: query.ec ? (1 as const) : undefined,
    r: (query.r as string) || undefined,
    bo: query.bo ? (1 as const) : undefined,
    of: query.of ? (1 as const) : undefined,
    sort: (query.sort as string) || undefined,
    q: (query.q as string) || undefined,
    fc: query.fc ? (1 as const) : undefined,
    limit: query.limit ? Number(query.limit) : 20,
  }
}

async function fetchCottagesData(boundsParams?: {
  ne?: string
  sw?: string
}): Promise<MapCottageType[]> {
  if (abortController) {
    abortController.abort()
  }

  abortController = new AbortController()
  const currentController = abortController

  isLoading.value = true
  const params = {
    ...parseUrlParams(),
    ...boundsParams,
  }

  try {
    const response = await fetchMapCottagesEndpoint(params)

    if (currentController.signal.aborted) {
      return []
    }

    if (response?.success?.data?.cottages) {
      const newCottages = response.success.data.cottages
      cottagesData.value = response.success.data

      if (
        isInitialLoad.value &&
        newCottages.length > 0 &&
        initialCottages.value.length === 0
      ) {
        initialCottages.value = [...newCottages]
        cottages.value = newCottages

        if (response.success.meta?.bounds) {
          apiBounds.value = response.success.meta.bounds
        }
      }

      return newCottages
    } else {
      return []
    }
  } catch (error) {
    if (currentController.signal.aborted) {
      return []
    }
    console.error('Error fetching cottages for map:', error)
    return []
  } finally {
    if (!currentController.signal.aborted) {
      isLoading.value = false
    }
  }
}

function parseLocation(
  latitude: string,
  longitude: string,
): [number, number] | null {
  try {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)
    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng]
    }
  } catch {
    console.error('Location Error')
  }
  return null
}

function calculateBounds(): [number, number][] | null {
  const validCoords: [number, number][] = []

  cottages.value.forEach(cottage => {
    const coords = parseLocation(cottage.latitude, cottage.longitude)
    if (coords) {
      validCoords.push(coords)
    }
  })

  if (validCoords.length === 0) return null

  return validCoords
}

function closePanel() {
  selectedCottage.value = null
}

function createMarkerIconApp(
  priceText: string,
  options: { isVisited?: boolean; isSelected?: boolean } = {},
): HTMLElement {
  const { isVisited = false, isSelected = false } = options
  const container = document.createElement('div')
  container.className = 'cottage-marker-icon-container'

  // eslint-disable-next-line vue/one-component-per-file
  const app = createApp({
    render() {
      const getVariant = () => {
        if (isSelected) return 'subtle'
        if (isVisited && !isSelected) return 'soft'
        return 'solid'
      }

      return h(
        UBadge,
        {
          color: 'neutral',
          variant: getVariant(),
          size: 'md',
          class: 'font-sans',
        },
        {
          default: () => priceText,
        },
      )
    },
  })

  app.mount(container)
  return container
}

function createPopupVueApp(cottage: MapCottageType): HTMLElement {
  const existingApp = popupApps.value.get(cottage.id)
  if (existingApp) {
    existingApp.unmount()
    popupApps.value.delete(cottage.id)
  }

  const container = document.createElement('div')
  container.className = 'cottage-popup-vue-container'

  // eslint-disable-next-line vue/one-component-per-file
  const app = createApp({
    render() {
      return h(CottageListMapPopup, {
        cottage,
        showClose: true,
        onClose: () => {
          markersMap.value.forEach(marker => {
            if (marker.getPopup()?.getContent() === container) {
              marker.closePopup()
            }
          })
        },
      })
    },
  })

  app.use(router)
  app.mount(container)
  popupApps.value.set(cottage.id, app)

  return container
}

function setupInitialView(L: any) {
  if (!map.value) return

  if (apiBounds.value && map.value) {
    const bounds = L.latLngBounds(
      [apiBounds.value.swLat, apiBounds.value.swLng],
      [apiBounds.value.neLat, apiBounds.value.neLng],
    )
    map.value.fitBounds(bounds, {
      padding: [10, 10],
      maxZoom: 18,
    })
  } else {
    const targetCottage = props.targetCottage
    if (targetCottage && markersMap.value.has(targetCottage.id)) {
      const targetMarker = markersMap.value.get(targetCottage.id)
      const targetLatLng = targetMarker.getLatLng()
      map.value.setView(targetLatLng, 14, { animate: true })

      setTimeout(() => {
        selectedCottage.value = targetCottage
      }, 300)
    } else {
      const coords = calculateBounds()
      if (coords && map.value) {
        const bounds = L.latLngBounds(coords)
        map.value.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: hasLocationParameter.value ? 14 : 12,
        })
      } else if (
        hasLocationParameter.value &&
        cottages.value.length === 0 &&
        props.destination
      ) {
        map.value.setView(
          [props.destination.latitude, props.destination.longitude],
          12,
          { animate: true },
        )
      }
    }
  }
}

function createDestinationMarkerIconApp(text: string): HTMLElement {
  const container = document.createElement('div')
  container.className = 'destination-marker-icon-container'

  // eslint-disable-next-line vue/one-component-per-file
  const app = createApp({
    render() {
      return h(
        'div',
        h(
          UBadge,
          {
            color: 'primary',
            variant: 'solid',
            size: 'md',
            icon: 'i-lucide-map-pin',
            class: 'truncate',
          },
          {
            default: () => text,
          },
        ),
      )
    },
  })

  app.mount(container)

  return container
}

function addDestinationMarker(L: any) {
  if (!map.value || !props.destination) return

  if (destinationMarker.value) {
    destinationMarker.value.remove()
    destinationMarker.value = null
  }

  const { latitude, longitude, name } = props.destination

  if (latitude && longitude) {
    const iconElement = createDestinationMarkerIconApp(name)
    const icon = L.divIcon({
      html: iconElement.outerHTML,
      className: '',
      iconSize: [null, 32],
      iconAnchor: [50, 32],
    })

    destinationMarker.value = L.marker([latitude, longitude], {
      icon,
    })

    destinationMarker.value.addTo(map.value)
  }
}

function addCottageMarkers(L: any) {
  if (map.value) {
    map.value.stop()
  }

  if (!map.value) return

  const currentCottageIds = new Set(cottages.value.map(c => c.id))

  markersMap.value.forEach((marker, cottageId) => {
    if (!currentCottageIds.has(cottageId)) {
      marker.remove()
      markersMap.value.delete(cottageId)

      const app = popupApps.value.get(cottageId)
      if (app) {
        app.unmount()
        popupApps.value.delete(cottageId)
      }
    }
  })

  cottages.value.forEach(cottage => {
    if (markersMap.value.has(cottage.id)) {
      return
    }
    const coords = parseLocation(cottage.latitude, cottage.longitude)
    if (coords && map.value) {
      const priceAmount = cottage.price?.amount ?? cottage.averagePrice.amount
      const priceText = toEuro(priceAmount)

      const isVisited = visitedCottages.value.has(cottage.id)
      const isSelected = selectedCottage.value?.id === cottage.id
      const iconElement = createMarkerIconApp(priceText, {
        isVisited,
        isSelected,
      })
      const customIcon = L.divIcon({
        html: iconElement.outerHTML,
        className: '',
        iconSize: [70, 32],
        iconAnchor: [35, 32],
      })

      const marker = L.marker(coords, {
        icon: customIcon,
        zIndexOffset: 1000,
      })

      marker.addTo(map.value)

      if (isDesktop) {
        marker.on('click', () => {
          visitedCottages.value.add(cottage.id)
          selectedCottage.value = cottage

          if (marker.getPopup()) {
            marker.unbindPopup()
          }

          let offsetX = 0
          let offsetY = 0

          if (map.value) {
            const markerContainerPoint = map.value.latLngToContainerPoint(
              marker.getLatLng(),
            )
            const mapSize = map.value.getSize()
            offsetY = markerContainerPoint.y < 325 ? 325 : -20
            offsetX =
              markerContainerPoint.x < 150
                ? 150
                : markerContainerPoint.x > mapSize.x - 150
                  ? -150
                  : offsetX
          }

          const popupContent = createPopupVueApp(cottage)
          const popup = L.popup({
            maxWidth: 384,
            className: 'cottage-leaflet-popup',
            autoClose: true,
            closeOnClick: false,
            autoPan: false,
            offset: [offsetX, offsetY],
          }).setContent(popupContent)

          popup.on('remove', () => {
            if (selectedCottage.value?.id === cottage.id) {
              selectedCottage.value = null
            }
            const app = popupApps.value.get(cottage.id)
            if (app) {
              app.unmount()
              popupApps.value.delete(cottage.id)
            }
          })

          marker.bindPopup(popup).openPopup()
        })
      } else {
        marker.on('click', () => {
          visitedCottages.value.add(cottage.id)

          if (selectedCottage.value?.id === cottage.id) {
            selectedCottage.value = null
          } else {
            selectedCottage.value = cottage
          }
        })
      }

      markersMap.value.set(cottage.id, marker)
    }
  })
}

async function updateMapBounds() {
  if (!map.value || !L) return

  if (isInitialLoad.value) {
    return
  }

  if (isUpdatingBounds.value) {
    return
  }

  if (updateBoundsTimeout) {
    clearTimeout(updateBoundsTimeout)
  }

  updateBoundsTimeout = setTimeout(async () => {
    isUpdatingBounds.value = true

    const bounds = map.value.getBounds()
    const northEast = bounds.getNorthEast()
    const southWest = bounds.getSouthWest()

    const ne = toCoords(northEast)
    const sw = toCoords(southWest)

    const newCottages = await fetchCottagesData({ ne, sw })

    if (hasLocationParameter.value && initialCottages.value.length > 0) {
      const cottageMap = new Map<string, MapCottageType>()

      initialCottages.value.forEach(cottage => {
        cottageMap.set(cottage.id, cottage)
      })

      newCottages.forEach(cottage => {
        cottageMap.set(cottage.id, cottage)
      })

      cottages.value = Array.from(cottageMap.values())
    } else {
      cottages.value = newCottages
    }

    if (L && map.value) {
      addCottageMarkers(L)
    }

    isUpdatingBounds.value = false
  }, 100)
}

onMounted(async () => {
  hasLocationParameter.value = !!route.query.l

  const LeafletModule = await import('leaflet')
  L = LeafletModule.default
  // @ts-ignore - leaflet.fullscreen doesn't have type declarations
  await import('leaflet.fullscreen')
  await import('leaflet/dist/leaflet.css')
  await import('leaflet.fullscreen/Control.FullScreen.css')
  await nextTick()

  if (leafletMap.value) {
    map.value = L.map(leafletMap.value, {
      center: defaultCenter,
      zoom,
      minZoom,
      maxZoom,
      fullscreenControl: false,
      zoomControl: false,
      zoomAnimation: true,
      fadeAnimation: true,
    } as CustomMapOptions)

    const zoomControl = (L.control as any).zoom({
      forceSeparateButton: false,
      zoomInTitle: t('views.cottage-list.map.zoom.in'),
      zoomOutTitle: t('views.cottage-list.map.zoom.out'),
    })
    zoomControl.addTo(map.value)

    const fullscreenControl = (L.control as any).fullscreen({
      forceSeparateButton: false,
      title: t('views.cottage-list.map.fullscreen.title'),
      titleCancel: t('views.cottage-list.map.fullscreen.titleCancel'),
    })
    fullscreenControl.addTo(map.value)

    const tileLayer = L.tileLayer(
      'https://mapas.escapadarural.com/hot/{z}/{x}/{y}.png',
      {
        maxZoom,
        attribution:
          'Map data: &copy; <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
      },
    )
    tileLayer.addTo(map.value)

    await fetchCottagesData()

    sendMapCottageStatsCloudwatch(cottagesData.value)

    addCottageMarkers(L)
    addDestinationMarker(L)

    setupInitialView(L)

    await new Promise(resolve => setTimeout(resolve, 500))

    if (hasLocationParameter.value) {
      const bounds = map.value.getBounds()
      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()

      const ne = toCoords(northEast)
      const sw = toCoords(southWest)

      const newCottages = await fetchCottagesData({ ne, sw })

      if (initialCottages.value.length > 0) {
        const cottageMap = new Map<string, MapCottageType>()

        initialCottages.value.forEach(cottage => {
          cottageMap.set(cottage.id, cottage)
        })

        newCottages.forEach(cottage => {
          cottageMap.set(cottage.id, cottage)
        })

        cottages.value = Array.from(cottageMap.values())
      } else {
        cottages.value = newCottages
      }

      addCottageMarkers(L)
    }

    map.value.on('moveend', () => {
      updateMapBounds()
    })

    isInitialLoad.value = false
  }
})

watch(
  () => route.query,
  async () => {
    if (!L || !map.value || isInitialLoad.value) return

    hasLocationParameter.value = !!route.query.l

    const bounds = map.value.getBounds()
    const northEast = bounds.getNorthEast()
    const southWest = bounds.getSouthWest()

    const ne = toCoords(northEast)
    const sw = toCoords(southWest)

    const newCottages = await fetchCottagesData({ ne, sw })

    if (hasLocationParameter.value && initialCottages.value.length > 0) {
      const cottageMap = new Map<string, MapCottageType>()

      initialCottages.value.forEach(cottage => {
        cottageMap.set(cottage.id, cottage)
      })

      newCottages.forEach(cottage => {
        cottageMap.set(cottage.id, cottage)
      })

      cottages.value = Array.from(cottageMap.values())
    } else {
      cottages.value = newCottages
    }

    if (L && map.value) {
      addCottageMarkers(L)
    }
  },
)

watch(selectedCottage, (newCottage, oldCottage) => {
  if (!L || !map.value) return

  if (newCottage) {
    sendMapCottageCardViz(newCottage.id)
  }

  if (oldCottage) {
    const oldMarker = markersMap.value.get(oldCottage.id)
    if (oldMarker) {
      const priceAmount =
        oldCottage.price?.amount ?? oldCottage.averagePrice.amount
      const priceText = toEuro(priceAmount)
      const isVisited = visitedCottages.value.has(oldCottage.id)

      const iconElement = createMarkerIconApp(priceText, {
        isVisited,
        isSelected: false,
      })
      const icon = L.divIcon({
        html: iconElement.outerHTML,
        className: '',
        iconSize: [70, 32],
        iconAnchor: [35, 32],
      })
      oldMarker.setIcon(icon)
    }
  }

  if (newCottage) {
    const newMarker = markersMap.value.get(newCottage.id)
    if (newMarker) {
      const priceAmount =
        newCottage.price?.amount ?? newCottage.averagePrice.amount
      const priceText = toEuro(priceAmount)
      const isVisited = visitedCottages.value.has(newCottage.id)

      const iconElement = createMarkerIconApp(priceText, {
        isVisited,
        isSelected: true,
      })
      const icon = L.divIcon({
        html: iconElement.outerHTML,
        className: '',
        iconSize: [70, 32],
        iconAnchor: [35, 32],
      })
      newMarker.setIcon(icon)
    }
  }
})

onBeforeUnmount(() => {
  if (updateBoundsTimeout) {
    clearTimeout(updateBoundsTimeout)
  }

  if (abortController) {
    abortController.abort()
  }

  popupApps.value.forEach(app => app.unmount())
  popupApps.value.clear()

  markersMap.value.forEach(marker => marker.remove())
  markersMap.value.clear()

  if (destinationMarker.value) {
    destinationMarker.value.remove()
    destinationMarker.value = null
  }

  if (map.value) {
    map.value.remove()
    map.value = null
  }
})

defineExpose({
  isLoading,
})
</script>

<style>
.cottage-leaflet-popup .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.cottage-leaflet-popup .leaflet-popup-content {
  margin: 0;
  width: 100% !important;
}

.leaflet-popup-tip {
  display: none;
}

.leaflet-popup-content p {
  margin: 0 !important;
}
</style>
